import {
  Badge,
  Box,
  Button,
  CopyButton,
  Group,
  Paper,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconPlus, IconRefresh } from '@tabler/icons-react';
import { useState, type FormEvent } from 'react';

import { useCreateDeviceLink } from '../api/createLink';

type Props = {
  onRefresh: () => void;
  isRefreshing: boolean;
};

export const DeviceTrackerHeader = ({ onRefresh, isRefreshing }: Props) => {
  const createLink = useCreateDeviceLink({
    config: {
      onSuccess: () => {
        notifications.show({ color: 'teal', title: 'Ссылка создана', message: '' });
        setLabel('');
        setSlug('');
      },
      onError: (error) => {
        const message =
          (error.response?.data as { error?: string })?.error || 'Не удалось создать ссылку';
        notifications.show({ color: 'red', title: 'Ошибка', message });
      },
    },
  });

  const [label, setLabel] = useState('');
  const [slug, setSlug] = useState('');

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const trimmedLabel = label.trim();
    if (!trimmedLabel) return;

    createLink.mutate({
      label: trimmedLabel,
      slug: slug.trim() || undefined,
    });
  };

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start">
        <div>
          <Title order={2}>Device Tracker</Title>
          <Text c="dimmed" size="sm" mt={4}>
            Уникальные ссылки для проверки устройств команды
          </Text>
        </div>
        <Button
          variant="light"
          leftSection={<IconRefresh size={16} />}
          loading={isRefreshing}
          onClick={onRefresh}
        >
          Обновить
        </Button>
      </Group>

      <Paper withBorder p="md" radius="md">
        <form onSubmit={handleSubmit}>
          <Group align="flex-end" wrap="wrap">
            <TextInput
              label="Метка"
              value={label}
              onChange={(event) => setLabel(event.currentTarget.value)}
              style={{ flex: 1, minWidth: 200 }}
              required
            />
            <TextInput
              label="Slug"
              placeholder="slug"
              value={slug}
              onChange={(event) => setSlug(event.currentTarget.value)}
              style={{ flex: 1, minWidth: 160 }}
            />
            <Button
              type="submit"
              leftSection={<IconPlus size={16} />}
              loading={createLink.isLoading}
            >
              Создать ссылку
            </Button>
          </Group>
        </form>
      </Paper>
    </Stack>
  );
};

export function MatchBadge({ verdict }: { verdict?: string | null }) {
  if (verdict === 'match') {
    return (
      <Badge color="teal" size="xs">
        MATCH
      </Badge>
    );
  }
  if (verdict === 'partial') {
    return (
      <Badge color="yellow" size="xs">
        ~MATCH
      </Badge>
    );
  }
  if (verdict === 'no_match') {
    return (
      <Badge color="red" size="xs">
        NO
      </Badge>
    );
  }
  return null;
}

export function ModelCell({
  phoneModel,
  gpuRenderer,
  screenKey,
  deviceId,
  modelSource,
  modelConfidence,
  matchVerdict,
}: {
  phoneModel?: string | null;
  gpuRenderer?: string | null;
  screenKey?: string | null;
  deviceId?: string | null;
  modelSource?: string | null;
  modelConfidence?: string | null;
  matchVerdict?: string | null;
}) {
  const mark = modelConfidence === 'approximate' ? ' ≈' : modelConfidence === 'likely' ? ' *' : '';

  return (
    <Stack gap={2}>
      <Group gap={6}>
        <Text size="sm">
          {phoneModel || '—'}
          {mark}
        </Text>
        {modelSource === 'native-app' && (
          <Badge variant="light" size="xs">
            app
          </Badge>
        )}
        <MatchBadge verdict={matchVerdict} />
      </Group>
      {gpuRenderer && gpuRenderer !== 'Unknown' && (
        <Text size="xs" c="dimmed" ff="monospace">
          {gpuRenderer}
        </Text>
      )}
      {screenKey && (
        <Text size="xs" c="dimmed" ff="monospace">
          {screenKey}
        </Text>
      )}
      {deviceId && (
        <Text size="xs" c="dimmed" ff="monospace">
          {deviceId}
        </Text>
      )}
    </Stack>
  );
}

export function LinkUrlRow({ url }: { url: string }) {
  return (
    <Group gap="xs">
      <Text component="a" href={url} target="_blank" rel="noopener noreferrer" size="sm" c="blue">
        {url}
      </Text>
      <CopyButton value={url}>
        {({ copied, copy }) => (
          <Button variant="subtle" size="compact-xs" onClick={copy}>
            {copied ? 'Скопировано' : 'Копировать'}
          </Button>
        )}
      </CopyButton>
    </Group>
  );
}

export function EmptyNote({ children }: { children: React.ReactNode }) {
  return (
    <Box py="md">
      <Text c="dimmed" size="sm">
        {children}
      </Text>
    </Box>
  );
}
